import { Icon } from "../";
import { cn } from "@/utils/cnHelper";
import { faCheck, faChevronDown, faChevronUp } from "@icons/regular";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as React from "react";

type Theme = "light" | "dark";

interface SelectProps extends React.ComponentProps<typeof SelectPrimitive.Root> {
  theme?: Theme;
}

const SelectContext = React.createContext<{ theme: Theme }>({ theme: "dark" });

function Select({ theme = "dark", ...props }: SelectProps) {
  return (
    <SelectContext.Provider value={{ theme }}>
      <SelectPrimitive.Root data-slot="select" {...props} />
    </SelectContext.Provider>
  );
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default";
}) {
  const { theme } = React.useContext(SelectContext);

  const themeClasses = {
    light: "tw-bg-white tw-border-gray-300 tw-text-gray-900 hover:tw-bg-gray-50 focus:tw-border-blue-500 focus:tw-ring-blue-500/20",
    dark: "tw-bg-grey_scale-900 tw-border-grey_scale-600 tw-text-white hover:tw-bg-grey_scale-800 focus:tw-border-shorthand_teal focus:tw-ring-shorthand_teal/20",
  };

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        // Base styles - use min-width to prevent layout shift, accommodate longest reasonable option + icons
        "tw-disabled:cursor-not-allowed tw-disabled:opacity-50 tw-relative tw-flex tw-w-full tw-min-w-[240px] tw-items-center tw-rounded-md tw-border tw-text-start tw-text-sm tw-shadow-sm tw-outline-none tw-transition-all",
        // Padding matches SelectItem (tw-px-3) but with extra right space for chevron + check icon space
        "tw-py-2 tw-pl-3 tw-pr-10",
        // Size variants
        "tw-data-[size=default]:h-9 tw-data-[size=sm]:h-8",
        // Theme styles
        themeClasses[theme],
        className
      )}
      {...props}
    >
      <div className="tw-flex-1 tw-truncate">{children}</div>
      <SelectPrimitive.Icon asChild>
        <Icon
          icon={faChevronDown}
          size="sm"
          className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-opacity-50"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = "popper",
  maxHeight, // Set maxHeight (e.g., "300px") to trigger scroll buttons when content overflows
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & {
  maxHeight?: string;
}) {
  const { theme } = React.useContext(SelectContext);

  const themeClasses = {
    light: "tw-bg-white tw-border-gray-200 tw-shadow-lg",
    dark: "tw-bg-grey_scale-900 tw-border-grey_scale-600 tw-shadow-lg",
  };

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "tw-z-50 tw-w-[var(--radix-select-trigger-width)] tw-max-w-full tw-overflow-hidden tw-rounded-lg tw-border",
          "tw-data-[state=open]:animate-in tw-data-[state=closed]:animate-out tw-data-[state=closed]:fade-out-0 tw-data-[state=open]:fade-in-0 tw-data-[state=closed]:zoom-out-95 tw-data-[state=open]:zoom-in-95",
          themeClasses[theme],
          className
        )}
        position={position}
        style={maxHeight ? { maxHeight } : undefined}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn("tw-px-1 tw-py-1", position === "popper" && "tw-h-[var(--radix-select-trigger-height)] tw-w-full")}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  const { theme } = React.useContext(SelectContext);

  const themeClasses = {
    light: "tw-text-grey_scale-400",
    dark: "tw-text-grey_scale-400",
  };

  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("tw-px-2 tw-py-1.5 tw-text-xs tw-font-medium", themeClasses[theme], className)}
      {...props}
    />
  );
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
  const { theme } = React.useContext(SelectContext);

  const themeClasses = {
    light:
      "tw-text-black hover:tw-bg-grey_scale-100 focus:tw-bg-grey_scale-1100 data-[highlighted]:tw-bg-grey_scale-100 data-[state=checked]:tw-bg-grey_scale-100",
    dark: "tw-text-white hover:tw-bg-grey_scale-700 focus:tw-bg-grey_scale-700 data-[highlighted]:tw-bg-grey_scale-700 data-[state=checked]:tw-bg-grey_scale-700",
  };

  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "tw-relative tw-flex tw-cursor-pointer tw-items-center tw-rounded-md tw-text-sm",
        // Padding matches SelectTrigger - left padding same, right padding accounts for check icon space
        "tw-py-2 tw-pl-3 tw-pr-10",
        "tw-outline-none tw-transition-colors data-[disabled]:tw-cursor-not-allowed data-[disabled]:tw-opacity-50",
        themeClasses[theme],
        className
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="tw-flex-1 tw-truncate">{children}</SelectPrimitive.ItemText>
      <span className="tw-absolute tw-right-3 tw-flex tw-items-center">
        <SelectPrimitive.ItemIndicator>
          <Icon icon={faCheck} size="sm" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  const { theme } = React.useContext(SelectContext);

  const themeClasses = {
    light: "tw-bg-gray-200",
    dark: "tw-bg-grey_scale-600",
  };

  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("tw-pointer-events-none -tw-mx-1 tw-my-1 tw-h-px", themeClasses[theme], className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  const { theme } = React.useContext(SelectContext);

  const themeClasses = {
    light: "tw-text-black",
    dark: "tw-text-white",
  };

  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn("tw-flex tw-cursor-default tw-items-center tw-justify-center tw-py-1", themeClasses[theme], className)}
      {...props}
    >
      <Icon icon={faChevronUp} size="sm" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  const { theme } = React.useContext(SelectContext);

  const themeClasses = {
    light: "tw-text-gray-600",
    dark: "tw-text-white",
  };

  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn("tw-flex tw-cursor-default tw-items-center tw-justify-center tw-py-1", themeClasses[theme], className)}
      {...props}
    >
      <Icon icon={faChevronDown} size="sm" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
export type { Theme };
